import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Form, Toast, InputGroup } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ApiEndpoint } from 'helpers/ApiEndPoint.js';
import { useAuth } from 'context/AuthProvider.js';
import logo from '../../assets/logo.png';
import bgImage from '../../assets/bg.jpg';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';

const LoginForm = ({ hasLabel = false }) => {
  const Auth = useAuth();
  let history = useNavigate();
  const [show, setShow] = useState(false); // State for Toast visibility
  const [formData, setFormData] = useState({
    user: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formErrorCount, setFormErrorCount] = useState(0);
  const [showPassword, setShowPassword] = useState(false); // Estado para mostrar/ocultar contraseña

  // Función de validación
  const validate = () => {
    const newErrors = {};
    if (!formData.user) newErrors.user = 'El usuario es obligatorio.';
    if (!formData.password)
      newErrors.password = 'La contraseña es obligatoria.';
    return newErrors;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setFormErrorCount(prev => prev + 1);
      setShow(true); // Mostrar el Toast si hay errores
      return;
    }
    setErrors({});
    setLoading(true);

    const URI = ApiEndpoint + 'users/login';
    axios
      .post(URI, {
        user: formData.user,
        password: formData.password
      })
      .then(function (response) {
        if (!response.data.error) {
          Auth.login(response.data);
          history('/');
        } else {
          setShow(true); // Mostrar el Toast si los datos son incorrectos
        }
      })
      .catch(function (error) {
        console.log(error);
        setShow(true); // Mostrar el Toast si hubo un error en la petición
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFieldChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <motion.div
      className="container-fluid vh-100 d-flex align-items-center justify-content-center bg-light"
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="row w-100">
        <div className="col-md-5 d-flex flex-column justify-content-center align-items-center p-5">
          <img
            src={logo}
            alt="logo"
            className="mb-4"
            style={{ maxWidth: 250 }}
          />
          <motion.div
            key={formErrorCount}
            className="card shadow rounded p-4 w-100"
            style={{ maxWidth: 400 }}
            animate={
              Object.keys(errors).length > 0 ? { x: [0, -10, 10, -10, 0] } : {}
            }
            transition={{ duration: 0.4 }}
          >
            <h4 className="mb-4">Iniciar sesión</h4>
            <Form onSubmit={handleSubmit}>
              {/* Campo usuario con icono */}
              <Form.Group className="mb-3">
                <Form.Label>Usuario</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faUser} className="text-secondary" />
                  </InputGroup.Text>
                  <Form.Control
                    isInvalid={!!errors.user}
                    placeholder={!hasLabel ? 'Usuario' : ''}
                    value={formData.user}
                    name="user"
                    onChange={handleFieldChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.user}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              {/* Campo contraseña con icono y toggle */}
              <Form.Group className="mb-3">
                <Form.Label>Contraseña</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faLock} className="text-secondary" />
                  </InputGroup.Text>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    isInvalid={!!errors.password}
                    placeholder={!hasLabel ? 'Contraseña' : ''}
                    value={formData.password}
                    name="password"
                    onChange={handleFieldChange}
                  />
                  <InputGroup.Text
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>

              {loading ? (
                <button className="btn btn-primary w-100" disabled>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Ingresando...
                </button>
              ) : (
                <button type="submit" className="btn btn-primary w-100">
                  Ingresar
                </button>
              )}
            </Form>
          </motion.div>

          {/* Toast de error */}
          <Toast
            show={show}
            onClose={() => setShow(false)}
            delay={5000}
            autohide
            bg="danger"
            className="position-fixed bottom-0 end-0 m-3"
          >
            <Toast.Body>
              Hubo un error al iniciar sesión. Verifica los datos ingresados.
            </Toast.Body>
          </Toast>

          {/* Footer */}
          <footer className="mt-4 text-center text-muted small">
            &copy; {new Date().getFullYear()} JMM Asesores. Todos los Derechos
            Reservados.
            <span className="d-none d-sm-inline-block"> | Design by </span>
            <a href="https://theseven.digital/"> The 7</a>
          </footer>
        </div>

        {/* Imagen de fondo */}
        <div className="col-md-7 d-none d-md-block p-0">
          <div
            className="h-100"
            style={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderTopLeftRadius: '20px',
              borderBottomLeftRadius: '20px'
            }}
          ></div>
        </div>
      </div>
    </motion.div>
  );
};

LoginForm.propTypes = {
  hasLabel: PropTypes.bool
};

LoginForm.defaultProps = {
  hasLabel: false
};

export default LoginForm;
